import { EngagementBuyerActionState } from '../../../slices/marketplace/timeline/models/engagement-buyer-action-state.model';
import { TimelineActionStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-action-status.enum';
import {
    TimelineEventStatus,
    TimelineEventStatuses,
    TimelineEventStatusesColorCode,
} from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import {
    TimelineParty,
    TimelinePartyColors,
} from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { TimelineStage } from '../../../slices/marketplace/timeline/models/timeline-stage.model';
import { GanttChartItem } from '../../GanttChartComponent/models/GanttChartItem.model';
import { STAGE_ITEMS } from '../Constants/dropdownItems';

interface TimelineSummary {
    stage: number;
    backgroundColor: string;
    dayStart: number;
    dayEnd: number;
}

export class DataGenerator {
    generateData(
        stages: TimelineStage[],
        events: TimelineEvent[],
        authorizedParty: TimelineParty,
        buyerActionState: EngagementBuyerActionState[],
        isEventClickable?: (eventId: string) => boolean
    ) {
        let data: GanttChartItem[] = [];
        const grouppedTimelineEvents = this.groupTimelineEvents(events);

        this.addPartyData(
            data,
            stages,
            events,
            grouppedTimelineEvents,
            authorizedParty,
            buyerActionState,
            isEventClickable
        );

        return data;
    }

    private addPartyData(
        data: GanttChartItem[],
        stages: TimelineStage[],
        events: TimelineEvent[],
        grouppedTimelineEvents: Record<number, TimelineSummary>,
        authorizedParty: TimelineParty,
        buyerActionState: EngagementBuyerActionState[],
        isEventClickable?: (eventId: string) => boolean
    ) {
        if (events.length > 0) {
            data.push({
                id: `Stages-header`,
                name: 'Stages',
                start: undefined,
                end: undefined,
                color: undefined,
                backgroundColor: undefined,
                orderNo: undefined,
                type: 'header',
                groupName: 'stages',
                isClickable: false,
                itemStatus: '',
                itemStatusColor: '',
                isBlinkAnimationActive: false,
            });

            for (let i of Object.values(grouppedTimelineEvents)) {
                const today = new Date();
                today.setHours(0);
                today.setDate(today.getDate() + i.dayStart);
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + i.dayEnd);
                const stage = stages.find((s) => s.id == i.stage);
                if (!stage) continue;
                data.push({
                    id: `stage-${i.stage}`,
                    name: stage.name,
                    start: today,
                    end: tomorrow,
                    color: 'black',
                    backgroundColor: i.backgroundColor,
                    orderNo: undefined,
                    type: 'header',
                    groupName: 'stages',
                    isClickable: false,
                    itemStatus: '',
                    itemStatusColor: '',
                    isBlinkAnimationActive: false,
                });
            }

            let count = 1;
            for (let event of events) {
                const effectiveDayStart = event.actualStartDay ?? event.dayStart;
                const today = new Date();
                today.setHours(0);
                today.setDate(today.getDate() + effectiveDayStart);
                const tomorrow = new Date();
                tomorrow.setDate(
                    tomorrow.getDate() + (event.dayCompleted ?? event.dayEnd)
                );
                const backgroundColor = TimelinePartyColors.find(
                    (p) => p.id === event.party
                )?.backgroundColor;
                const color =
                    event.status !== TimelineEventStatus.Completed &&
                    isEventClickable &&
                    isEventClickable(event.id)
                        ? '#545cd8'
                        : 'black';
                const isBuyerPartyEvent =
                    event.party === TimelineParty.Buyer ||
                    event.party === TimelineParty.SellerAndBuyer;
                const isBuyerCompletedEvent =
                    authorizedParty === TimelineParty.Buyer &&
                    isBuyerPartyEvent &&
                    event.linkedAction != null &&
                    buyerActionState.some(
                        (as) =>
                            as.actionId === event.linkedAction.id &&
                            as.status === TimelineActionStatus.Completed
                    );
                const eventStatus = isBuyerCompletedEvent
                    ? TimelineEventStatus.Completed
                    : event.isSuspendEvent &&
                        event.status === TimelineEventStatus.Late
                      ? TimelineEventStatus.InProgress
                      : event.status;

                data.push({
                    id: `event-${event.id}`,
                    name: event.name,
                    start: today,
                    end: tomorrow,
                    color: color,
                    backgroundColor: event.isSuspendEvent
                        ? '#ced4da'
                        : backgroundColor,
                    orderNo: count,
                    type: 'line',
                    isClickable: isEventClickable && isEventClickable(event.id),
                    itemStatus:
                        TimelineEventStatuses.find(
                            (f) =>
                                f.id === eventStatus &&
                                f.id !== TimelineEventStatus.Upcoming
                        )?.name ?? '',
                    itemStatusColor:
                        TimelineEventStatusesColorCode.find(
                            (f) => f.id === eventStatus
                        )?.color ?? 'black',
                    isBlinkAnimationActive: this.isEventBlinkAnimationActive(
                        event,
                        authorizedParty,
                        buyerActionState
                    ),
                });
                count++;
            }
        }
    }

    private isEventBlinkAnimationActive(
        event: TimelineEvent,
        authorizedParty: TimelineParty,
        buyerActionState: EngagementBuyerActionState[]
    ): boolean {
        if (event.party !== authorizedParty || !!!event.linkedAction) {
            return false;
        }

        if (authorizedParty === TimelineParty.Buyer) {
            const doesBuyerCompleteEvent = buyerActionState.some(
                (actionState) =>
                    actionState.actionId === event?.linkedAction?.id &&
                    actionState.status === TimelineActionStatus.Completed
            );

            return (
                event.status === TimelineEventStatus.Late &&
                !doesBuyerCompleteEvent
            );
        }

        return event.status === TimelineEventStatus.Late;
    }

    groupTimelineEvents(
        events: TimelineEvent[]
    ): Record<number, TimelineSummary> {
        const result: Record<number, TimelineSummary> = {};
        const stagesOrder: number[] = [];
        const stageColors = new Map<number, string>();
        let globalMaxDayEnd = 0;

        events.forEach((event) => {
            const { stage, dayStart, dayEnd, dayCompleted, actualStartDay } =
                event;
            const effectiveDayStart = actualStartDay ?? dayStart;
            const effectiveDayEnd = dayCompleted ?? dayEnd;

            globalMaxDayEnd = Math.max(globalMaxDayEnd, effectiveDayEnd);

            if (!stageColors.has(stage)) {
                const color = STAGE_ITEMS[stageColors.size % STAGE_ITEMS.length].color;
                stageColors.set(stage, color);
                stagesOrder.push(stage);
            }

            if (!result[stage]) {
                result[stage] = {
                    stage,
                    backgroundColor: stageColors.get(stage),
                    dayStart: effectiveDayStart,
                    dayEnd: effectiveDayEnd,
                };
            } else {
                result[stage].dayStart = Math.min(
                    result[stage].dayStart,
                    effectiveDayStart
                );
                result[stage].dayEnd = Math.max(
                    result[stage].dayEnd,
                    effectiveDayEnd
                );
            }
        });

        for (let i = 0; i < stagesOrder.length - 1; i++) {
            const currentStage = result[stagesOrder[i]];
            const nextStageMinStart = result[stagesOrder[i + 1]].dayStart;
            currentStage.dayEnd = Math.min(currentStage.dayEnd, nextStageMinStart);
        }

        if (stagesOrder.length > 0) {
            result[stagesOrder[stagesOrder.length - 1]].dayEnd = globalMaxDayEnd;
        }

        return result;
    }
}
